import { useNavigate } from 'react-router-dom';

import type { PersonaAvatarProfile } from '@repo/contracts';

import { trackEvent } from '../analytics/posthog';
import { UserMenu } from '../auth/user-menu';
import { CreditsPill } from '../credits/credits-pill';
import { CenteredShell } from '../marketing/centered-shell';
import { PersonaAvatar } from '../persona/persona-avatar';
import { AccountSwitcher } from '../tenant/account-switcher';
import { LinkField } from './link-field';

/**
 * Panel members waiting around the field, each with the kind of thing a
 * room says. Real portraits in the panel's own DiceBear style, the faces
 * fixed so they match the sign-in screen's. `place` is relative to the
 * content column, always outside it — so a bubble can sit beside the field
 * but never on it. They hold still: moving bubbles beside a field you are
 * typing into pull the eye away from it.
 */
export const GREETERS: {
  line: string;
  name: string;
  place: string;
  profile: PersonaAvatarProfile;
  tilt: string;
}[] = [
  {
    line: 'Is it keyboard friendly?',
    name: 'Arjun',
    place: 'top-[6%] right-full mr-12',
    profile: {
      clothes: 'squared',
      clothingColor: '456dff',
      eyes: 'open',
      facialHair: 'beardMustache',
      hair: 'shortCombover',
      hairColor: '362c47',
      mouth: 'smirk',
      nose: 'mediumRound',
      skinColor: 'b16a5b',
    },
    tilt: '-rotate-3',
  },
  {
    line: 'When does it arrive?',
    name: 'Hana',
    place: 'top-0 left-full ml-12',
    profile: {
      clothes: 'rounded',
      clothingColor: 'f55d81',
      eyes: 'happy',
      hair: 'bobBangs',
      hairColor: 'e15c66',
      mouth: 'smile',
      nose: 'smallRound',
      skinColor: 'e5a07e',
    },
    tilt: 'rotate-3',
  },
  {
    line: 'Show me already!',
    name: 'Ava',
    place: 'bottom-0 right-full mr-8',
    profile: {
      clothes: 'rounded',
      clothingColor: '54d7c7',
      eyes: 'happy',
      hair: 'long',
      hairColor: '362c47',
      mouth: 'bigSmile',
      nose: 'smallRound',
      skinColor: '92594b',
    },
    tilt: 'rotate-2',
  },
  {
    line: 'What’s the price?',
    name: 'Kofi',
    place: 'bottom-[12%] left-full ml-8',
    profile: {
      clothes: 'checkered',
      clothingColor: 'f3b63a',
      eyes: 'glasses',
      facialHair: 'goatee',
      hair: 'fade',
      hairColor: '362c47',
      mouth: 'smile',
      nose: 'wrinkles',
      skinColor: '623d36',
    },
    tilt: '-rotate-2',
  },
];

/**
 * The first visit, before there is anything of their own to show: a welcome,
 * the one field that starts everything, and a few of the people who will
 * react to what they paste, drifting around it.
 */
export function FirstRun() {
  const navigate = useNavigate();

  return (
    <CenteredShell
      top={
        <>
          <CreditsPill />
          <AccountSwitcher />
          <UserMenu />
        </>
      }
      width="max-w-2xl"
    >
      <div className="relative flex w-full max-w-lg flex-col items-center">
        <Greeters />

        {/* Title, underline and line under it, 4px apart, as designed. */}
        <div className="flex flex-col items-center gap-[4px]">
          <h1 className="relative !m-0 !text-auth-display !font-bold !text-text-primary duration-700 animate-in fade-in slide-in-from-bottom-3 motion-reduce:animate-none">
            {/* The yellow dot from the design, off the headline's left. */}
            <span
              aria-hidden="true"
              className="absolute top-4 -left-14 hidden size-6 rounded-pill bg-sunny sm:block"
            />
            Welcome to{' '}
            <img
              alt="Rooms"
              className="inline-block h-[28px] w-auto align-baseline sm:h-[32px]"
              src="/icons/rooms.svg"
            />
          </h1>
          <img
            alt=""
            aria-hidden="true"
            className="w-[260px] duration-700 animate-in fade-in motion-reduce:animate-none"
            src="/zero-state/squiggle-underline.svg"
          />
          <p className="max-w-[483px] text-body tracking-[-0.04em] text-text-secondary duration-700 animate-in fade-in slide-in-from-bottom-3 motion-reduce:animate-none">
            See how different people might respond to your page. Paste a link to
            create your first room and get feedback from AI personas.
          </p>
        </div>

        <div className="mt-[20px] w-full">
          <LinkField
            onStart={(url) => {
              trackEvent('session_started', { source: 'import' });
              void navigate(`/browse?url=${encodeURIComponent(url)}`);
            }}
            variant="hero"
          />
        </div>
      </div>
    </CenteredShell>
  );
}

/**
 * Laid out against the content column, outside its edges. Off below `xl`,
 * where there is no room beside the column for them. Decorative, so hidden
 * from assistive tech.
 */
export function Greeters({
  places,
}: {
  /** Where each one sits on this screen, by name, in place of `place`. */
  places?: Record<string, string>;
} = {}) {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 hidden select-none xl:block"
    >
      {GREETERS.map((greeter) => (
        <div
          className={`absolute ${places?.[greeter.name] ?? greeter.place}`}
          key={greeter.name}
        >
          <div>
            <div
              className={`flex h-[56px] items-center gap-[10px] rounded-pill bg-surface py-[8px] pr-[16px] pl-[8px] whitespace-nowrap shadow-bubble duration-700 animate-in fade-in ${greeter.tilt}`}
            >
              <PersonaAvatar
                persona={{
                  accent: 0,
                  avatarProfile: greeter.profile,
                  id: `first-run-${greeter.name}`,
                  name: greeter.name,
                }}
                size="bubble"
              />
              <span className="flex flex-col text-left leading-tight">
                <span className="text-tag font-bold text-text-muted">
                  {greeter.name}
                </span>
                <span className="font-heading text-ui-lg font-bold text-text-primary">
                  {greeter.line}
                </span>
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
